/* eslint-disable ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes */
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { reads } from '@ember/object/computed';
import { tracked } from '@glimmer/tracking';
import { tagName } from '@ember-decorators/component';

/**
 * Insights::LearnerDrilldown is the progress overview's side panel, listing
 * the learners behind whatever was selected. Every learner starts ticked, and
 * a group made from the panel holds only those still ticked.
 *
 * @param {boolean} isOpen
 * @param {Object} context - the selection's title and, for a cohort flow
 *   selection, its milestone labels.
 * @param {string} description
 * @param {string} rule - the on-track rule the listing is read against.
 * @param {Array<{title, count, rule, learners}>} groups - the learners, in
 *   sections by Milestone kind when there is more than one.
 * @param {Array<Object>} people - the listed learners' person records.
 * @param {string} groupName - what a group made from the panel is called to
 *   begin with.
 * @param {Function} onClose
 */
@tagName('')
export default class LearnerDrilldown extends Component {
  /** @type {Array<Object>|null} */
  groups = null;

  /** @type {Array<Object>|null} */
  people = null;

  /** @type {Array<string>} The ids of the ticked learners. */
  @tracked selectedIds = null;

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    // A new selection starts with everyone ticked.
    if (this.people !== this._seenPeople) {
      this._seenPeople = this.people;
      this.selectedIds = (this.people || []).map((person) => person.id);
    }
  }

  // Each learner with their person record, for the photo, and whether they
  // are ticked.
  @computed('groups.[]', 'people.[]', 'selectedIds.[]')
  get rows() {
    let people = new Map(
      (this.people || []).map((person) => [person.id, person]),
    );
    let selected = new Set(this.selectedIds || []);
    return (this.groups || []).map((group) =>
      Object.assign({}, group, {
        learners: group.learners.map((learner) =>
          Object.assign({}, learner, {
            person: people.get(learner.id),
            initials: (learner.fullName || '')
              .split(' ')
              .map((word) => word[0])
              .slice(0, 2)
              .join('')
              .toUpperCase(),
            isSelected: selected.has(learner.id),
          }),
        ),
      }),
    );
  }

  @computed('people.[]', 'selectedIds.[]')
  get selectedPeople() {
    let selected = new Set(this.selectedIds || []);
    return (this.people || []).filter((person) => selected.has(person.id));
  }

  @reads('selectedPeople.length')
  selectedCount;

  @computed('people.length', 'selectedCount')
  get allSelected() {
    return !!this.people?.length && this.selectedCount === this.people.length;
  }

  @computed('allSelected', 'selectedCount')
  get someSelected() {
    return this.selectedCount > 0 && !this.allSelected;
  }

  @action
  toggleLearner(learner) {
    let ids = this.selectedIds || [];
    this.selectedIds = ids.includes(learner.id)
      ? ids.filter((id) => id !== learner.id)
      : ids.concat(learner.id);
  }

  @action
  toggleAll() {
    this.selectedIds = this.allSelected
      ? []
      : (this.people || []).map((person) => person.id);
  }
}
